import { MetalsQuotePriceBreakLine } from '../models/metals-quote.model';

const MIN_MULTIPLIER = 0.01;

/**
 * Suggest a price-break tier at a particular position without changing either
 * surrounding tier. Between two tiers we interpolate on log(quantity) and
 * log(multiplier), which follows the power curve used by the quote generator.
 */
export function suggestMetalsQuotePriceBreak(
    previous: MetalsQuotePriceBreakLine | null,
    next: MetalsQuotePriceBreakLine | null,
    discountRatePercent: number,
): MetalsQuotePriceBreakLine | null {
    const previousIsUsable = isUsableTier(previous);
    const nextIsUsable = isUsableTier(next);

    if (!previousIsUsable && !nextIsUsable) return { quantity: 1, multiplier: 1 };

    if (previousIsUsable && nextIsUsable) {
        if (next.quantity - previous.quantity <= 1) return null;
        const quantity = practicalQuantityBetween(previous.quantity, next.quantity);
        const progress = Math.log(quantity / previous.quantity)
            / Math.log(next.quantity / previous.quantity);
        const multiplier = Math.exp(
            Math.log(previous.multiplier)
            + progress * (Math.log(next.multiplier) - Math.log(previous.multiplier)),
        );
        return { quantity, multiplier: roundMultiplier(multiplier) };
    }

    const anchor = previousIsUsable ? previous : next;
    if (!anchor) return { quantity: 1, multiplier: 1 };
    const quantity = previousIsUsable
        ? practicalQuantityAfter(anchor.quantity)
        : practicalQuantityBefore(anchor.quantity);
    if (quantity === null) return null;

    const rate = Math.min(0.99, Math.max(0, Number(discountRatePercent) / 100 || 0));
    const multiplier = anchor.multiplier
        * Math.pow(1 - rate, Math.log2(quantity / anchor.quantity));
    return { quantity, multiplier: roundMultiplier(multiplier) };
}

/** Return a user-facing validation error for one ordered price-break row. */
export function metalsQuotePriceBreakError(
    lines: MetalsQuotePriceBreakLine[],
    index: number,
): string {
    const line = lines[index];
    if (!line || !Number.isFinite(line.quantity) || line.quantity <= 0 || !Number.isInteger(line.quantity)) {
        return 'quantity must be a positive whole number';
    }
    if (!Number.isFinite(line.multiplier) || line.multiplier <= 0) {
        return 'multiplier must be greater than zero';
    }
    if (lines.some((other, otherIndex) => otherIndex !== index && other.quantity === line.quantity)) {
        return 'quantity must be unique';
    }
    if (index > 0 && line.quantity <= lines[index - 1].quantity) {
        return 'move this tier so quantities increase';
    }
    return '';
}

function isUsableTier(
    tier: MetalsQuotePriceBreakLine | null,
): tier is MetalsQuotePriceBreakLine {
    return !!tier
        && Number.isFinite(tier.quantity)
        && tier.quantity > 0
        && Number.isFinite(tier.multiplier)
        && tier.multiplier > 0;
}

function practicalQuantityBetween(lower: number, upper: number): number {
    const target = Math.sqrt(lower * upper);
    const snapped = nearestPracticalQuantity(target);
    if (snapped > lower && snapped < upper) return snapped;
    return Math.min(Math.ceil(upper) - 1, Math.max(Math.floor(lower) + 1, Math.round(target)));
}

function practicalQuantityAfter(quantity: number): number {
    const target = quantity * 2;
    return Math.max(Math.floor(quantity) + 1, nearestPracticalQuantity(target));
}

function practicalQuantityBefore(quantity: number): number | null {
    if (quantity <= 1) return null;
    const target = quantity / 2;
    const snapped = nearestPracticalQuantity(target);
    return Math.max(1, Math.min(Math.ceil(quantity) - 1, snapped));
}

/** Snap to familiar quoting quantities while retaining useful mid-range values. */
function nearestPracticalQuantity(value: number): number {
    if (!(value > 0)) return 1;
    const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
    const steps = [1, 1.25, 1.5, 2, 2.5, 3, 4, 5, 6, 7.5, 8, 10];
    return Math.max(1, Math.round(steps
        .map(step => step * magnitude)
        .reduce((best, candidate) => Math.abs(candidate - value) < Math.abs(best - value)
            ? candidate
            : best)));
}

function roundMultiplier(value: number): number {
    return Math.max(MIN_MULTIPLIER, Math.round(value * 100) / 100);
}
